import React, { Component } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { selfShape } from '../../utils/propTypes';
import {
    dataTransform,
    webhookEventTypes,
    transferStatusMapper,
    doesPropExistAndHasItChanged
} from '@nexio/emvio-util-app';

import config from '../../config/config';
import Table from '../Table/Table';
import ColorDollarAmountCell from '../Table/Cells/ColorDollarAmountCell/ColorDollarAmountCell';
import FilterWithMapper from '../Table/Filters/FilterWithMapper/FilterWithMapper';
import GenericDropDownFilter from '../Table/Filters/GenericDropDownFilter/GenericDropDownFilter';
import PayoutStatusFilter from '../Table/Filters/PayoutStatusFilter/PayoutStatusFilter';
import { CurrencyCodeFilter } from '../Table/Filters/CurrencyCodeFilter/CurrencyCodeFilter';
import { fetchWebhooks, selectWebhook, resendWebhookLogs } from './webhookLogActions';
import {
    getIsWebhooksLoading,
    getWebhooks,
    getWebhooksErrorMessage,
    getWebhookCount,
    getWebhookPages,
    getWebhookResend,
    getIsWebhookResendLoading,
    getWebhookResendErrorMessage,
    getWebhookHasMore
} from './webhookLogEntity';
import { getUserRole } from '../UserData/userDataEntity';
import { getIsLoading as getIsUserLoading, getSelf } from '../Self/selfEntity';
import './WebhookLog.scss';
import { toast } from 'react-toastify';
import { localGatewayAndApmMapper } from '../../utils/gateways';
import { doesRoleHaveAccessToFeature } from '../../utils/customRole';
import { getGateways } from '../Gateways/gatewaysEntity';
import { getApms } from '../Apms/apmEntity';
import moment from 'moment-timezone';

export class WebhookLog extends Component {
    static propTypes = {
        isUserLoading: PropTypes.bool,
        isWebhooksLoading: PropTypes.bool,
        isWebhookResendLoading: PropTypes.bool,
        isWebhookResendSuccess: PropTypes.bool,
        isWebhookResendError: PropTypes.bool,
        userRole: PropTypes.any,
        user: selfShape,
        gateways: PropTypes.shape({
            gatewayList: PropTypes.object,
            gatewaysThatCanCapture: PropTypes.array,
            gatewaysThatCanRefund: PropTypes.array,
            gatewaysThatCanVoid: PropTypes.array
        }),
        apms: PropTypes.array
    };

    constructor(props) {
        super(props);

        this.dateKey = 'webhookDate';

        this.defaultColumnsAndProps = [
            {
                label: 'Webhook Date',
                accessor: this.dateKey,
                defaultSortDesc: true,
                transformData: dataTransform.dateTimezoneButHideTimezone,
                transformType: 'dateTimezoneButHideTimezone',
                minWidth: 180,
                className: ['cursor-pointer'],
                filterable: false,
                show: true
            },
            {
                label: 'Event Type',
                accessor: 'eventType',
                Filter: ({ filter, onChange }) => (
                    <FilterWithMapper
                        filter={filter}
                        mapper={webhookEventTypes}
                        onChange={onChange}
                    />
                ),
                minWidth: 240,
                className: ['cursor-pointer'],
                show: true
            },
            {
                label: 'Webhook Status',
                accessor: 'responseCode',
                transformData: (data) => data.value || 'Not Configured',
                minWidth: 160,
                className: ['cursor-pointer'],
                filterable: true,
                show: true,
                sortable: true
            },
            {
                label: 'Merchant ID',
                accessor: 'merchantId',
                filterable: false,
                minWidth: 150,
                className: ['cursor-pointer'],
                show: true,
                sortable: true
            },
            {
                label: 'Payout Account ID',
                accessor: 'payoutAccountId',
                filterable: false,
                minWidth: 200,
                className: ['cursor-pointer'],
                show: false,
                sortable: true
            },
            {
                label: 'Transaction Date',
                accessor: 'transactionDetails.transactionDate',
                transformData: dataTransform.dateTimezoneButHideTimezone,
                filterable: false,
                minWidth: 200,
                className: ['cursor-pointer'],
                show: true
            },
            {
                label: 'Gateway',
                accessor: 'transactionDetails.gatewayType',
                transformData: (data) => data.value,
                Filter: ({ filter, onChange }) => (
                    <GenericDropDownFilter
                        filter={filter}
                        onChange={onChange}
                        values={_.sortBy(_.values(localGatewayAndApmMapper(this.props.gateways, this.props.apms)))}
                    />),
                minWidth: 150,
                className: ['cursor-pointer'],
                show: true
            },
            {
                label: 'Gateway Ref Number',
                accessor: 'transactionDetails.refNumber',
                minWidth: 190,
                className: ['cursor-pointer'],
                show: true
            },
            {
                label: 'Amount',
                accessor: 'requestBody.data.data.amount',
                Cell: (props) => <ColorDollarAmountCell
                    amount={props.value}
                    currencyCode={_.get(props, 'original.requestBody.data.data.currency')}
                    overrideText=" "
                />,
                minWidth: 150,
                className: ['cursor-pointer'],
                filterable: false,
                show: true,
                sortable: false
            },
            {
                label: 'Currency',
                accessor: 'requestBody.data.data.currency',
                Filter: ({ filter, onChange }) => (
                    <CurrencyCodeFilter
                        filter={filter}
                        onChange={onChange}
                    />
                ),
                minWidth: 150,
                className: ['cursor-pointer'],
                filterable: false,
                show: true,
                sortable: false
            },
            {
                label: 'Auth Code',
                accessor: 'requestBody.data.authCode',
                minWidth: 150,
                className: ['cursor-pointer'],
                filterable: false,
                show: true,
                sortable: false
            },
            {
                label: 'Card Holder',
                accessor: 'requestBody.data.card.cardHolder',
                minWidth: 180,
                className: ['cursor-pointer'],
                filterable: false,
                show: true,
                sortable: false
            },
            {
                label: 'Card Number',
                accessor: 'requestBody.data.card.cardNumber',
                transformData: dataTransform.ellipsisNumber,
                minWidth: 150,
                className: ['cursor-pointer'],
                filterable: false,
                show: true,
                sortable: false
            },
            {
                label: 'Payment ID',
                accessor: 'transactionDetails.paymentId',
                minWidth: 150,
                className: ['cursor-pointer'],
                filterable: true,
                show: true,
                sortable: false
            },
            {
                label: 'Payout Status',
                accessor: 'payoutDetails.payoutStatus',
                transformData: dataTransform.payoutStatusMapper,
                Filter: ({ filter, onChange }) => (
                    <PayoutStatusFilter
                        filter={filter}
                        onChange={onChange}
                    />
                ),
                minWidth: 150,
                className: ['cursor-pointer'],
                show: false
            },
            {
                label: 'Transfer Status',
                accessor: 'payoutDetails.transferStatus',
                transformData: dataTransform.transferStatusMapper,
                Filter: ({ filter, onChange }) => (
                    <FilterWithMapper
                        filter={filter}
                        mapper={transferStatusMapper}
                        onChange={onChange}
                    />
                ),
                minWidth: 170,
                className: ['cursor-pointer'],
                show: false
            },
            {
                label: 'Transaction Status',
                accessor: 'requestBody.data.transactionStatus',
                minWidth: 185,
                className: ['cursor-pointer'],
                filterable: false,
                sortable: false,
                show: false
            },
            {
                label: 'Retry ID',
                accessor: 'declineRecoveryDetails.retryId',
                minWidth: 150,
                className: ['cursor-pointer'],
                filterable: true,
                show: true,
                sortable: false
            },
            {
                label: 'Order Number',
                accessor: 'transactionDetails.orderId',
                minWidth: 150,
                className: ['cursor-pointer'],
                filterable: true,
                show: true,
                sortable: false
            }
        ];
    }

    componentDidUpdate(prevProps) {
        if (doesPropExistAndHasItChanged(prevProps, this.props, 'isWebhookResendSuccess')) {
            toast('Webhook(s) Resent', { type: 'success' });
        }

        if (doesPropExistAndHasItChanged(prevProps, this.props, 'isWebhookResendError')) {
            toast('Failed to resend webhooks', { type: 'error' });
        }
    }

    render() {

        const { customAccess } = _.get(this.props, 'user') || {};
        let hasCustomWebhookReadAccess = false;
        if (customAccess && this.props.userRole === 'R') {
            const { serviceArns } = config;
            if (doesRoleHaveAccessToFeature(customAccess, { [serviceArns.webhook]: ['GET/v3'] })) {
                hasCustomWebhookReadAccess = true;
            }
        }

        if ((this.props.userRole === 'R' && !hasCustomWebhookReadAccess) && !this.props.isUserLoading) {
            return (<div className="emvio-webhook-log">
                Insufficient access rights to view webhook logs.
            </div>);
        }
        const bulkActions = [{
            ButtonLabel: 'Resend',
            confirmationText: 'Are you sure you would like to resend these webhooks?',
            loading: this.props.isWebhookResendLoading,
            onClick: resendWebhookLogs
        }];

        return (
            <div className="emvio-webhook-log">
                <div className="header-container">
                    <div className="header-text">Webhooks</div>
                </div>
                <Table
                    initialStartDate={moment().tz('UTC').startOf('day')}
                    initialEndDate={moment().tz('UTC').endOf('day')}
                    fetchUrl={config.webhookApiUrl}
                    bulkActions={bulkActions}
                    className="emvio-webhook-log-table"
                    dateKey={this.dateKey}
                    defaultColumnsAndProps={this.defaultColumnsAndProps}
                    defaultSorted={[{
                        desc: true,
                        id: this.dateKey
                    }]}
                    fetchData={fetchWebhooks}
                    filterable={true}
                    countSelector={getWebhookCount}
                    dataSelector={getWebhooks}
                    hasNoCount={true}
                    hasMoreSelector={getWebhookHasMore}
                    errorMessageSelector={getWebhooksErrorMessage}
                    isDataLoadingSelector={getIsWebhooksLoading}
                    isActionLoading={this.props.isWebhooksLoading}
                    pagesSelector={getWebhookPages}
                    selectRow={selectWebhook}
                    shouldAddCheckboxColumn
                    shouldUseGlobalDate
                    itemKey="logId"
                    ListItemComponent={() => <div />}
                    noDataText="No Webhooks Logs"
                    shouldHideExport
                    uniqueTableName="WebhookLog"
                    timezone="UTC"
                />
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        isUserLoading: getIsUserLoading(state),
        isWebhooksLoading: getIsWebhooksLoading(state),
        isWebhookResendLoading: getIsWebhookResendLoading(state),
        isWebhookResendSuccess: getWebhookResend(state),
        isWebhookResendError: getWebhookResendErrorMessage(state),
        userRole: getUserRole(state),
        gateways: getGateways(state),
        apms: getApms(state),
        user: getSelf(state)
    };
}

export default connect(mapStateToProps)(WebhookLog);
